import { supabase } from './lib/supabase'
import { initChat } from './chat'
import './style.css'

// Intersection Observer for scroll-reveal animations
const observerOptions = {
  threshold: 0.1,
  rootMargin: '0px 0px -50px 0px'
};

const observer = new IntersectionObserver((entries) => {
  entries.forEach(entry => {
    if (entry.isIntersecting) {
      entry.target.classList.add('visible');
      // Once visibility is triggered, we can stop observing this element
      observer.unobserve(entry.target);
    }
  });
}, observerOptions);

let isInitialized = false;

async function init() {
  if (isInitialized) return;
  
  // Observe all elements with the 'fade-in' class
  const revealElements = document.querySelectorAll('.fade-in');
  revealElements.forEach(el => observer.observe(el));

  setupHeaderScroll();
  setupTestimonialSlider();
  setupFAQAccordion();
  setupMobileMenu();
  
  isInitialized = true;
}

function setupFAQAccordion() {
  const triggers = document.querySelectorAll('.faq-trigger');
  
  triggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const item = trigger.closest('.faq-item');
      const content = item.querySelector('.faq-content');
      const icon = trigger.querySelector('.faq-icon');
      
      const isOpen = item.classList.contains('active');
      
      // Close all other items
      document.querySelectorAll('.faq-item').forEach(otherItem => {
        if (otherItem !== item) {
          otherItem.classList.remove('active');
          const otherContent = otherItem.querySelector('.faq-content');
          const otherIcon = otherItem.querySelector('.faq-icon');
          if (otherContent) otherContent.style.height = 'px';
          if (otherIcon) otherIcon.style.transform = 'rotate(0deg)';
        }
      });
      
      if (isOpen) {
        item.classList.remove('active');
        content.style.height = 'px';
        icon.style.transform = 'rotate(0deg)';
      } else {
        item.classList.add('active');
        content.style.height = content.scrollHeight + 'px';
        icon.style.transform = 'rotate(45deg)';
      }
    });
  });
}

function setupTestimonialSlider() {
  const slider = document.getElementById('testimonial-slider');
  const prevBtn = document.getElementById('prev-btn');
  const nextBtn = document.getElementById('next-btn');

  if (!slider || !prevBtn || !nextBtn) return;

  const getScrollAmount = () => {
    const firstCard = slider.querySelector('.snap-card');
    return firstCard ? firstCard.offsetWidth + 24 : 480;
  };

  nextBtn.addEventListener('click', () => {
    slider.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
  });

  prevBtn.addEventListener('click', () => {
    slider.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
  });
}

function setupHeaderScroll() {
  const header = document.querySelector('.header');
  if (!header) return;

  const mobileBreadcrumb = document.getElementById('mobile-breadcrumb');
  const breadcrumbCurrent = document.getElementById('breadcrumb-current');
  const sections = document.querySelectorAll('section[id]');

  window.addEventListener('scroll', () => {
    const scrollY = window.scrollY;

    // Header scroll effect - More opaque zinc-950 for better contrast on white
    if (scrollY > 50) {
      header.classList.add('py-4', 'bg-zinc-950/95', 'border-white/10');
      header.classList.remove('py-0', 'bg-zinc-950/90', 'border-white/5');
    } else {
      header.classList.remove('py-4', 'bg-zinc-950/95', 'border-white/10');
      header.classList.add('py-0', 'bg-zinc-950/90', 'border-white/5');
    }

    // Mobile Breadcrumb visibility
    if (mobileBreadcrumb) {
      if (scrollY > 300) {
        mobileBreadcrumb.classList.remove('-translate-y-full');
        mobileBreadcrumb.classList.add('translate-y-0');
      } else {
        mobileBreadcrumb.classList.add('-translate-y-full');
        mobileBreadcrumb.classList.remove('translate-y-0');
      }

      // Scroll Spy for Breadcrumb Text
      sections.forEach(section => {
        const sectionTop = section.offsetTop - 100;
        const sectionHeight = section.offsetHeight;
        const sectionId = section.getAttribute('id');

        if (scrollY >= sectionTop && scrollY < sectionTop + sectionHeight) {
          const sectionName = sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
          if (breadcrumbCurrent) breadcrumbCurrent.textContent = sectionName.replace('-', ' ');
        }
      });
    }
  });
}

function setupMobileMenu() {
  const menu = document.getElementById('mobile-menu');
  const openBtn = document.getElementById('mobile-menu-open');
  const closeBtn = document.getElementById('mobile-menu-close');
  const breadcrumbTrigger = document.getElementById('breadcrumb-menu-trigger');
  const links = menu?.querySelectorAll('a');

  if (!menu) return;

  const toggleMenu = (show) => {
    if (show) {
      menu.classList.remove('translate-x-full');
      menu.classList.add('translate-x-0');
      document.body.style.overflow = 'hidden';
    } else {
      menu.classList.add('translate-x-full');
      menu.classList.remove('translate-x-0');
      document.body.style.overflow = '';
    }
  };

  openBtn?.addEventListener('click', () => toggleMenu(true));
  closeBtn?.addEventListener('click', () => toggleMenu(false));
  breadcrumbTrigger?.addEventListener('click', () => toggleMenu(true));

  // Close menu when a link is clicked
  links?.forEach(link => {
    link.addEventListener('click', () => toggleMenu(false));
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && menu.classList.contains('translate-x-0')) {
      toggleMenu(false);
    }
  });
}

// Bootstrapper
const start = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
};

start();
window.addEventListener('pageshow', (event) => {
  if (event.persisted) init();
});
